<!-- テナント設定 > 請求書フォーム -->
<template>
  <div>
    <validation-observer ref="BillingAddressFormValidator">
      <v-card v-if="billing_address" flat>
        <v-toolbar flat>
          <v-toolbar-title>{{ $route.params.id ? '請求先編集' : '請求先追加' }}</v-toolbar-title>
        </v-toolbar>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="3">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ required: true, alpha_num_only: true, min: 4, max: 10 }"
                name="請求先コード"
                class="requiredMark"
              >
                <v-text-field
                  v-model="billing_address.code"
                  :disabled="billing_address.disabled_billing_address"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="請求先コード"
                  outlined
                  persistent-placeholder
                  placeholder="例）B001"
                >
                  <template v-slot:label>
                    <span>請求先コード</span>
                    <span class="ml-1">
                      ({{ billing_address.code?.length || 0 }} / 10)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              最近使用したコード:&nbsp;<strong>{{ billing_address.recent_registered_codes }}</strong>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="請求先名"
                rules="required|max:60"
              >
                <v-text-field
                  v-model="billing_address.name"
                  :disabled="billing_address.disabled_billing_address"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="請求先名"
                  outlined
                  persistent-placeholder
                  placeholder="例）△△△△株式会社"
                >
                  <template v-slot:label>
                    <span>請求先名</span>
                    <span class="ml-1">
                      ({{ billing_address.name?.length || 0 }} / 60)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>

          <!-- 郵便番号 -->
          <v-row>
            <v-col cols="12" md="5">
              <validation-provider
                v-slot="{ errors }"
                :rules="{zip_code: true, required: true, max:8 }"
                name="郵便番号"
                class="requiredMark"
              >
                <v-text-field
                  v-model="billing_address.zip_code"
                  :disabled="billing_address.disabled_billing_address"
                  :error-messages="errors"
                  dense
                  label="郵便番号"
                  hide-details="auto"
                  hint="ハイフンを含めて入力してください"
                  outlined
                  persistent-hint
                  persistent-placeholder
                  placeholder="例）111-1111"
                  required
                >
                  <template v-slot:label>
                    <span>郵便番号</span>
                    <span class="ml-1">
                      ({{ billing_address.zip_code?.length || 0 }} / 8)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                  <template v-slot:append-outer>
                    <zip-code-search v-if="!billing_address.disabled_billing_address"
                      :zip-code="billing_address.zip_code"
                      @zipAddress="setZipAddress"
                    />
                    &nbsp;
                    <v-btn
                      outlined
                      small
                      @click="searchZipButtonClickEvent"
                    >
                      住所→郵便番号
                    </v-btn>
                  </template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /郵便番号 -->

          <!-- 都道府県／市区町村 -->
          <v-row>
            <v-col cols="12" md="3">
              <validation-provider
                v-slot="{ errors }"
                name="都道府県"
                rules="required"
                class="requiredMark"
              >
                <v-autocomplete
                  v-model="billing_address.prefecture_code"
                  :disabled="billing_address.disabled_billing_address"
                  :error-messages="errors"
                  :items="prefList"
                  dense
                  hide-details="auto"
                  item-text="text"
                  item-value="value"
                  label="都道府県"
                  outlined
                  persistent-placeholder
                  placeholder="例）東京都"
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="市区町村"
                rules="required|full_width|max:20"
                class="requiredMark"
              >
                <v-text-field
                  v-model="billing_address.address_1"
                  :disabled="billing_address.disabled_billing_address"
                  :error-messages="errors"
                  dense
                  label="市区町村"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）中央区"
                >
                  <template v-slot:label>
                    <span>市区町村</span>
                    <span class="ml-1">
                      ({{ billing_address.address_1?.length || 0 }} / 20)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /都道府県／市区町村 -->

          <!-- 町域 -->
          <v-row>
            <v-col cols="12" md="9">
              <validation-provider
                v-slot="{ errors }"
                name="町域"
                rules="full_width|max:40"
              >
                <v-text-field
                  v-model="billing_address.address_2"
                  :disabled="billing_address.disabled_billing_address"
                  :error-messages="errors"
                  dense
                  label="町域"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）日本橋兜町"
                >
                  <template v-slot:label>
                    <span>町域</span>
                    <span class="ml-1">
                      ({{ billing_address.address_2?.length || 0 }} / 40)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /町域 -->

          <!-- 詳細住所 -->
          <v-row>
            <v-col md="9">
              <validation-provider
                v-slot="{ errors }"
                name="詳細住所"
                rules="max:50"
              >
                <v-text-field
                  v-model="billing_address.address_3"
                  :disabled="billing_address.disabled_billing_address"
                  :error-messages="errors"
                  dense
                  label="詳細住所"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）１－２－３"
                >
                  <template v-slot:label>
                    <span>詳細住所</span>
                    <span class="ml-1">
                      ({{ billing_address.address_3?.length || 0 }} / 50)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /詳細住所 -->

          <!-- 電話番号 -->
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="電話番号"
                :rules="{ numeric_hyphen_only: true,min: 2, max: 15 }"
              >
                <v-text-field
                  v-model="billing_address.phone_number"
                  :disabled="billing_address.disabled_billing_address"
                  :error-messages="errors"
                  dense
                  hint="電話番号を「-」ハイフン「( )」込みで入力します"
                  persistent-hint
                  label="電話番号"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder="例）000-0000-0000"
                >
                  <template v-slot:label>
                    <span>電話番号</span>
                    <span class="ml-1">
                      ({{ billing_address.phone_number?.length || 0 }} / 15)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                name="メモ欄"
                rules="max:30"
              >
                <v-text-field
                  v-model="billing_address.memo"
                  :error-messages="errors"
                  dense
                  label="メモ欄"
                  hide-details="auto"
                  outlined
                  persistent-placeholder
                  placeholder=""
                >
                  <template v-slot:label>
                    <span>メモ欄</span>
                    <span class="ml-1">
                      ({{ billing_address.memo?.length || 0 }} / 30)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /電話番号 -->

          <v-row>
            <v-col cols="12" md="4">
              <validation-provider
                v-slot="{ errors }"
                :rules="{ alpha_num_only: true, length: 14 }"
                name="登録番号（適格請求書発行事業者）"
              >
                <v-text-field
                  v-model="billing_address.register_number"
                  :disabled="billing_address.disabled_billing_address"
                  :error-messages="errors"
                  dense
                  hide-details="auto"
                  label="登録番号（適格請求書発行事業者）"
                  outlined
                  persistent-placeholder
                  placeholder="例）T1234567890123"
                >
                  <template v-slot:label>
                    <span>登録番号（適格請求書発行事業者）</span>
                    <span class="ml-1">
                      ({{ billing_address.register_number?.length || 0 }} / 14)
                    </span>
                  </template>
                  <template v-slot:counter></template>
                </v-text-field>
              </validation-provider>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-title>
          <span style="color: #0074BA">締め日等</span>
        </v-card-title>
        <v-card-text>
          <!-- 会社内で請求先を共有 -->
          <v-row>
            <v-col cols="12" md="6">
              <v-checkbox
                v-model="billing_address.use_share"
                label="会社内で請求先を共有する"
                :disabled="billing_address.billing_tenant_name"
                hide-details="auto"
                class="pa-0 ma-0"
              />
              <span v-if=" billing_address.billing_tenant_name">
                請求先テナント: {{ billing_address.billing_tenant_name }}
              </span>
            </v-col>
          </v-row>
          <!-- /会社内で請求先を共有-->

          <!-- 締め日/支払いサイト -->
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="締め日"
                rules="required"
              >
                <v-select
                  v-model="billing_address.close_day_type"
                  :disabled="billing_address.disabled_billing_address"
                  :items="closeDayTypes"
                  :error-messages="errors"
                  dense
                  label="締め日"
                  outlined
                  persistent-placeholder
                  hide-details="auto"
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="支払いサイト"
                rules="required"
              >
                <v-select
                  v-model="billing_address.payment_site"
                  :disabled="billing_address.disabled_billing_address"
                  :items="paymentSites"
                  :error-messages="errors"
                  dense
                  label="支払いサイト"
                  outlined
                  persistent-placeholder
                  hide-details="auto"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /締め日/支払いサイト -->

          <!-- 入金日/入金方法 -->
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="入金日"
                rules="required"
              >
                <v-select
                  v-model="billing_address.payment_day_type"
                  :disabled="billing_address.disabled_billing_address"
                  :items="paymentDayTypes"
                  :error-messages="errors"
                  dense
                  label="入金日"
                  outlined
                  persistent-placeholder
                  hide-details="auto"
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="入金方法"
                rules="required"
              >
                <v-select
                  v-model="billing_address.payment_method_type"
                  :disabled="billing_address.disabled_billing_address"
                  :items="paymentMethodTypes"
                  :error-messages="errors"
                  dense
                  label="入金方法"
                  outlined
                  persistent-placeholder
                  hide-details="auto"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /入金日/入金方法 -->

          <!-- 相殺区分/端数処理区分 -->
          <v-row>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="相殺区分"
                rules="required"
              >
                <v-select
                  v-model="billing_address.counterbalance_type"
                  :disabled="billing_address.disabled_billing_address"
                  :items="counterbalanceTypes"
                  :error-messages="errors"
                  dense
                  label="相殺区分"
                  outlined
                  persistent-placeholder
                  hide-details="auto"
                />
              </validation-provider>
            </v-col>
            <v-col cols="12" md="6">
              <validation-provider
                v-slot="{ errors }"
                class="requiredMark"
                name="端数処理区分"
                rules="required"
              >
                <v-select
                  v-model="billing_address.fraction_type"
                  :disabled="billing_address.disabled_billing_address"
                  :items="fractionTypes"
                  :error-messages="errors"
                  dense
                  label="端数処理区分"
                  outlined
                  persistent-placeholder
                  hide-details="auto"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <!-- /相殺区分/端数処理区分 -->

        </v-card-text>

        <v-divider />

        <template v-if="userFreeeClaim">
          <v-card-title>
            <span style="color: #0074BA">連携先のfreee取引先</span>
          </v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" md="6">
                <validation-provider
                  name="freee取引先"
                >
                  <v-text-field
                    v-model="billing_address.freee_partner_name"
                    dense
                    filled
                    hide-details="auto"
                    label="freee取引先"
                    outlined
                    persistent-hint
                    persistent-placeholder
                    readonly
                  >
                    <template v-slot:append-outer>
                      <v-btn
                        color="primary"
                        small
                        @click="enableFreeePartnerDialog=true"
                      >
                        取引先を選択
                      </v-btn>
                    </template>
                  </v-text-field>
                </validation-provider>
              </v-col>
            </v-row>
          </v-card-text>
          <v-divider />
        </template>
        <v-card-title>
          <v-spacer />
          <choose-supplier-button
            :button-caption="`事業者を選択`"
            :withoutInvisible = true
            with-none-location
            @choseSupplierCallback="choseSupplierCallback"
          />
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col>
              <v-data-table
                :headers="tableHeaders"
                :items="filteredBillingAddressDetails"
                :items-per-page="-1"
                hide-default-footer
                disable-sort
                disable-pagination
              >
                <template v-slot:[`item.manifest_type`]="{ item }">
                  {{ item.manifest_type | manifest_type_to_label }}
                </template>
                <template v-slot:[`item.address`]="{ item }">
                  <span>{{ item.address | truncate(25) }}</span>
                </template>
                <template v-slot:[`item.created_at`]="{item}">
                  {{ item.created_at | unix_datetime_format }}
                </template>
                <template v-slot:[`item.editable`]="{ item }">
                  <v-btn
                    v-if="item.can_remove"
                    icon
                    color="error"
                    @click="removeButtonClickEvent(item)"
                  >
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            @click="cancelButtonClickEvent"
          >
            閉じる
          </v-btn>
          <v-btn
            color="primary"
            @click="registerButtonClickEvent"
          >
            登録
          </v-btn>
        </v-card-actions>
      </v-card>
    </validation-observer>
    <confirm ref="confirm" />
    <v-snackbar
      v-model="enableDuplicatedMessage"
      color="red accent-2"
    >
      {{ snackBarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="enableDuplicatedMessage = false"
        >
          閉じる
        </v-btn>
      </template>
    </v-snackbar>
    <freee-partner-dialog
      :enable-dialog="enableFreeePartnerDialog"
      @cancelCallback="closeDialogCallback"
      @registerCallback="freeePartnerCallback"
    />
    <search-zip-modal
      :enable="enableSearchZipModal"
      :initial-prefecture-code="billing_address.prefecture_code"
      :initial-city-name="billing_address.address_1"
      :initial-town-name="billing_address.address_2"
      @reflectFullCallback="searchZipReflectCallback"
      @reflectZipCallback="searchZipReflectZipCallback"
      @closeCallback="closeModalCallback"
    />
  </div>
</template>

<script>

import {mapActions, mapState} from 'vuex'
import Confirm from '@/components/Confirm'
import ChooseSupplierButton from '@/driver/components/ChooseSupplierButton'
import PrefectureList from '../../../../mixins/PrefectureList'
import ZipCodeSearch from '../../../../components/ZipCodeSearch'
import FreeePartnerDialog from './_FreeePartnerDialog.vue'
import SearchZipModal from "../../../components/SearchZipModal.vue";

export default {
  name: 'BillingAddressForm',
  components: {
    SearchZipModal,
    Confirm,
    ChooseSupplierButton,
    ZipCodeSearch,
    FreeePartnerDialog,
  },
  mixins: [ PrefectureList ],
  data () {
    return {
      snackBarMessage: null,
      enableDuplicatedMessage: false,
      enableEmissionDialog: false,
      enableTraderDialog: false,
      enableFreeePartnerDialog: false,
      enableSearchZipModal: false,
      billing_address: null,
      /* テナント */
      tenants: [],
      /* 締め日 */
      closeDayTypes: [],
      /* 支払サイト */
      paymentSites: [],
      /* 入金日 */
      paymentDayTypes: [],
      /* 入金方法 */
      paymentMethodTypes: [],
      /* 相殺区分 */
      counterbalanceTypes: [],
      /* 端数処理区分 */
      fractionTypes: [],
      /* freee取引先 */
      freeePartners: [],
      tableHeaders: [
        { value: 'subscriber_number', text: '加入者番号' },
        { value: 'code', text: '事業者コード' },
        { value: 'name', text: '事業者名' },
        { value: 'address', text: '住所' },
        { value: 'manifest_type', text: 'マニフェスト区分' },
        { value: 'created_at', text: '登録日時' },
        { value: 'editable', text: '削除', align: 'end' },
      ],
    }
  },
  computed: {
    ...mapState('DriverTenantConfiguration', {
      userFreeeClaim: 'use_freee_claim',
    }),
    filteredBillingAddressDetails () {
      let returnList = []
      for (const detail of this.billing_address.billing_address_details) {
        if (!detail._destroy) {
          returnList.push(detail)
        }
      }
      return returnList
    },
  },
  watch: {
  },
  created() {

    this.fetchTenants().then((content) => {
      this.tenants = content.tenants
    })

    if (this.$route.params.id) {
      this.editBillingAddress({ id: this.$route.params.id }).then((content) => {
        this.billing_address = content
        this.closeDayTypes = content.close_day_types
        this.paymentSites = content.payment_sites
        this.paymentDayTypes = content.payment_day_types
        this.paymentMethodTypes = content.payment_method_types
        this.counterbalanceTypes = content.counterbalance_types
        this.fractionTypes = content.fraction_types
      })
    } else {
      this.newBillingAddress().then((content) => {
        this.billing_address = content
        this.closeDayTypes = content.close_day_types
        this.paymentSites = content.payment_sites
        this.paymentDayTypes = content.payment_day_types
        this.paymentMethodTypes = content.payment_method_types
        this.counterbalanceTypes = content.counterbalance_types
        this.fractionTypes = content.fraction_types
      })
    }

    this.loadFreeePartners();
  },
  methods: {
    ...mapActions('DriverMasterBillingAddress', {
      newBillingAddress: 'new',
      editBillingAddress: 'edit',
      createBillingAddress: 'create',
      updateBillingAddress: 'update'
    }),
    ...mapActions('DriverMasterBillingAddressRestrictionConfirm', {
      restrictionConfirm: 'show',
    }),
    ...mapActions('DriverMasterSearchTenant', {
      fetchTenants: 'index',
    }),
    ...mapActions("DriverProviderFreeePartner", {
      fetchFreeePartners: "index"
    }),
    searchZipButtonClickEvent () {
      this.enableSearchZipModal = true;
    },
    removeButtonClickEvent (item) {
      let modalOption = { title: '削除', message: '削除しますがよろしいですか？', options: { width: 320, titleColor: 'red', isCancelButton: true } }
      this.$refs.confirm.open(modalOption).then((confirm) => {
        this.$refs.confirm.close()
        if (confirm) {
          for (const billing_address_detail of this.billing_address.billing_address_details) {
            if (billing_address_detail.key === item.key) {
              billing_address_detail._destroy = true
            }
          }
        }
      })
    },
    registerButtonClickEvent () {
      let $self = this
      this.$refs.BillingAddressFormValidator.validate().then((result) => {
        if(result){
          $self.sendRegistrationInformation()
        }
      })
    },
    sendRegistrationInformation(){

      let details = []
      for (const billing_address_detail of this.billing_address.billing_address_details) {
        let detail = {
          id: billing_address_detail.id,
          elementable_id: billing_address_detail.elementable_id,
          elementable_type: billing_address_detail.elementable_type,
          tenant_id: billing_address_detail.tenant_id,
          _destroy: billing_address_detail._destroy,
        }
        details.push(detail)
      }

      for (const freeePartner of this.freeePartners) {
        if (this.billing_address.freee_partner_id === freeePartner.value) {
          this.billing_address.freee_partner_name = freeePartner.text;
        }
      }


      const params = {
        use_share: this.billing_address.use_share,
        register_number: this.billing_address.register_number,
        code: this.billing_address.code,
        name: this.billing_address.name,
        zip_code: this.billing_address.zip_code,
        prefecture_code: this.billing_address.prefecture_code,
        address_1: this.billing_address.address_1,
        address_2: this.billing_address.address_2,
        address_3: this.billing_address.address_3,
        phone_number: this.billing_address.phone_number,
        close_day_type: this.billing_address.close_day_type,
        payment_site: this.billing_address.payment_site,
        payment_day_type: this.billing_address.payment_day_type,
        payment_method_type: this.billing_address.payment_method_type,
        counterbalance_type: this.billing_address.counterbalance_type,
        fraction_type: this.billing_address.fraction_type,
        memo: this.billing_address.memo,
        freee_partner_id: this.billing_address.freee_partner_id,
        freee_partner_name: this.billing_address.freee_partner_name,
        billing_address_details_attributes: details,
      }

      if (this.$route.params.id) {
        this.updateBillingAddress({ id: this.$route.params.id, params: params }).then(() => {
          this.$router.push( { name: 'DriverMasterBillingAddresses' } )
        })
      } else {
        this.createBillingAddress({ params: params }).then(() => {
          this.$router.push( { name: 'DriverMasterBillingAddresses', params: { resetPagination: true } } )
        })
      }
    },
    cancelButtonClickEvent: function() {
      this.$router.push({ name: 'DriverMasterBillingAddresses' })
    },
    setZipAddress (content) {
      this.billing_address.prefecture_code = `${content.pref_code}`
      this.billing_address.address_1 = `${content.locality}`
      this.billing_address.address_2 = `${content.street}`
    },
    choseSupplierCallback (item) {

      // 一覧に重複する事業者が存在する場合はエラー
      let duplicate_record = this.billing_address.billing_address_details.find (v =>  v.elementable_id === item.id && v.elementable_type === item.type && !v._destroy )
      if (duplicate_record) {
        this.snackBarMessage = 'すでに選択されている事業者が含まれています。'
        this.enableDuplicatedMessage = true
        return
      }
      const params = {
        billing_address_id: this.$route.params.id,
        supplier_id: item.id,
        supplier_type: item.type,
      }
      // 他の請求先に紐ついているかチェックを実施
      this.restrictionConfirm({ params: params }).then((content) => {
        if (content.can_add) {
          this.addSupplierToList(item)
        } else {
          this.snackBarMessage = '事業者は他の請求先で使用されています。'
          this.enableDuplicatedMessage = true
        }
      })
    },
    /**
     * 事業者リストにデータを追加する.
     * @param item
     */
    addSupplierToList (item) {
      let detail = {
        id: null,
        key: Math.round( Math.random()*1000000 ),
        name: item.name,
        address: item.address,
        subscriber_number: item.subscriber_number,
        code: item.code,
        elementable_id: item.id,
        elementable_type: item.type,
        tenant_id: null,
        manifest_type: item.manifest_type,
        can_remove: true,
        created_at: null,
        _destroy: false,
      }
      this.billing_address.billing_address_details.push(detail)
    },
    loadFreeePartners () {
      this.fetchFreeePartners().then((content) => {
        this.freeePartners = content.partners
      })
    },
    closeDialogCallback () {
      this.enableFreeePartnerDialog = false
    },
    freeePartnerCallback (item) {
      this.billing_address.freee_partner_id = item.id;
      this.billing_address.freee_partner_name = item.name;
      this.closeDialogCallback()
    },
    closeModalCallback () {
      this.enableSearchZipModal = false;
    },
    /**
     * 郵便番号検索反映ボタン コールバック
     * @param address { Object }
     * @property address.zip_code { string } 郵便番号
     * @property address.prefecture_code { number } 都道府県コード
     * @property address.city { string } 市区町村
     * @property address.town { string } 町域
     */
    searchZipReflectCallback (address) {
      this.billing_address.zip_code = address.zip_code
      this.billing_address.prefecture_code = `${address.prefecture_code}`
      this.billing_address.address_1 = address.city
      this.billing_address.address_2 = address.town
    },
    searchZipReflectZipCallback (zipCode) {
      this.billing_address.zip_code = zipCode;
    },
  }
}
</script>
